// Content data for Sang Made Diva Agastya LLC landing page

export const company = {
  name: "Sang Made Diva Agastya LLC",
  shortName: "Sang Made Diva Agastya",
  suffix: "LLC",
  email: "info@agastya-llc.com",
  phone: "+1 888 557 6994",
  phoneHref: "+18885576994",
  address: {
    line1: "30 N Gould St Ste R",
    line2: "Sheridan, WY 82801, USA",
  },
  registered: "Wyoming, USA",
  anchor: "London W1D",
};

export const pillars = [
  {
    n: "01",
    title: "Conflict-free",
    desc: "A clean fee model \u2014 no listings, no vendor cuts, no hidden margins between us and your outcome.",
  },
  {
    n: "02",
    title: "Analytical",
    desc: "Yield modelling, layout economics, and jurisdictional review that turn instinct into evidence.",
  },
  {
    n: "03",
    title: "Distance-proof",
    desc: "Technology and audit protocols that let a property in another timezone still feel governed.",
  },
];

export const marqueeCities = [
  "London \u00B7 W1D",
  "Mayfair",
  "Knightsbridge",
  "Belgravia",
  "Manhattan",
  "Tribeca",
  "Singapore",
  "Monaco",
  "Hong Kong",
  "Lisbon",
];

export const challenges = [
  {
    n: "01",
    title: "Jurisdictional complexity",
    desc: "Tax codes, licensing regimes, and tenancy rules shift by borough and by year. Owners often discover the fine print only after it costs them.",
  },
  {
    n: "02",
    title: "Opaque vendors",
    desc: "Agents, managers, and trades on the ground rarely price on merit alone \u2014 information gaps quietly compound into lost yield.",
  },
  {
    n: "03",
    title: "The time-zone tax",
    desc: "A minor issue at 8am London becomes an expensive one by the time an overseas owner is awake to authorise action.",
  },
  {
    n: "04",
    title: "Advisors on the wrong side",
    desc: "Commissions, rebates, and management fees are structured for the middlemen. Rarely are they structured for you.",
  },
];

export const services = [
  {
    n: "01",
    icon: "Compass",
    title: "Owner-side Portfolio Advisory",
    subtitle: "Yield & Layout Blueprint",
    desc: "A written engagement that models rental return, redesigns interior layouts for premium tenants, and audits regulatory exposure \u2014 asset by asset.",
    bullets: [
      "Micro-market yield model per unit",
      "Interior re-planning for higher rents",
      "Tenancy & licensing exposure review",
    ],
    fee: "Fixed engagement \u00B7 $2.5k \u2013 $5k / property",
  },
  {
    n: "02",
    icon: "HardHat",
    title: "Refurbishment Oversight",
    subtitle: "Vendor Sourcing & Site Audits",
    desc: "We select trades, verify scope, and audit every milestone on your behalf \u2014 all while capital and payments remain entirely under your control.",
    bullets: [
      "Trade shortlist & background checks",
      "On-site milestone & materials audits",
      "Invoice review & dispute handling",
    ],
    fee: "Success-linked \u00B7 12% of contract value",
  },
  {
    n: "03",
    icon: "Cpu",
    title: "PropTech Operations",
    subtitle: "Deployment & Monthly Retainers",
    desc: "Management software, keyless access, cleaning cycles, and a maintenance supply chain \u2014 stood up, integrated, and monitored so the property runs itself.",
    bullets: [
      "PMS, smart-lock & keyless setup",
      "Cleaning & repair supply chain",
      "Monthly remote performance review",
    ],
    fee: "Retainer \u00B7 $1,500 / month / unit",
  },
];

export const steps = [
  {
    n: "01",
    title: "Introductory Call",
    desc: "A private, no-fee conversation to understand your holdings, the markets involved, and what a successful mandate looks like for you.",
  },
  {
    n: "02",
    title: "Written Mandate",
    desc: "A tailored engagement document laying out yield assumptions, layout ideas, and compliance findings for each asset in scope.",
  },
  {
    n: "03",
    title: "Coordinated Rollout",
    desc: "We line up trades, PropTech partners, and site teams \u2014 then audit every milestone and cost so nothing drifts unnoticed.",
  },
  {
    n: "04",
    title: "Ongoing Review",
    desc: "Quarterly portfolio reviews, occupancy and yield dashboards, and a documented trail so decisions compound over time.",
  },
];

export const specStats = [
  { label: "Primary focus", value: "London", sub: "W1, SW1, SW3, NW3" },
  { label: "Asset class", value: "Prime residential", sub: "Single units \u2192 8-unit blocks" },
  { label: "Typical mandate", value: "\u00A31.2M \u2013 \u00A315M", sub: "Single asset value" },
  { label: "Client geography", value: "6 continents", sub: "Absentee owners only" },
];

export const whyUs = [
  {
    icon: "ShieldCheck",
    title: "One-sided fees",
    desc: "Our income comes from a single source \u2014 you. No listing splits, no referral kickbacks, no vendor rebates in the background.",
  },
  {
    icon: "Scale",
    title: "The owner\u2019s voice in the room",
    desc: "We hold no listings, run no funds, and rent out no units. Our job, in every meeting, is to speak for the person who signs the cheque.",
  },
  {
    icon: "Sparkles",
    title: "Evidence over instinct",
    desc: "Every recommendation is backed by yield data, spatial economics, and jurisdictional reading \u2014 not by mood or market gossip.",
  },
  {
    icon: "Globe",
    title: "US structure, London depth",
    desc: "A US-registered practice with a working bench of trades, compliance advisors, and hospitality partners across prime London postcodes.",
  },
  {
    icon: "Lock",
    title: "Your capital, your control",
    desc: "We never hold client funds. Rent, deposits, and vendor payments stay between you and the licensed parties involved.",
  },
  {
    icon: "CircleCheck",
    title: "Quiet by default",
    desc: "Every engagement is written, private, and reported through channels you approve. Discretion is a working assumption, not a promise.",
  },
];

export const pricing = [
  {
    tag: "Yield & Layout Blueprint",
    title: "Advisory",
    price: "$2,500 \u2013 $5,000",
    unit: "fixed fee \u00B7 per property",
    features: [
      "Introductory scoping & objective mapping",
      "Micro-market yield model",
      "Interior layout blueprint",
      "Tenancy & licensing review",
      "Written engagement report",
    ],
    cta: "Start with an advisory",
    highlight: false,
  },
  {
    tag: "Refurbishment & Vendor Oversight",
    title: "Project Oversight",
    price: "12%",
    unit: "of refurbishment contract value",
    features: [
      "Trade sourcing & background checks",
      "Milestone & quality audits",
      "Materials & invoice verification",
      "Independent dispute mediation",
      "Final walk-through & sign-off",
    ],
    cta: "Engage on a project",
    highlight: true,
  },
  {
    tag: "PropTech & Ongoing Care",
    title: "Portfolio Retainer",
    price: "$1,500",
    unit: "per month \u00B7 per unit",
    features: [
      "PMS, smart-lock & keyless setup",
      "Cleaning & maintenance supply chain",
      "Monthly remote performance review",
      "Quarterly portfolio review",
      "Priority incident response",
    ],
    cta: "Hold the portfolio",
    highlight: false,
  },
];

export const aboutPoints = [
  {
    n: "01",
    title: "American structure, global roster",
    desc: "A US-registered firm running remote mandates for clients on six continents \u2014 built for people who live in one time zone and invest in another.",
  },
  {
    n: "02",
    title: "London as our working home",
    desc: "Our deepest bench is in prime London \u2014 W1, SW1, SW3, NW3 \u2014 across trades, compliance, and hospitality partners in the city\u2019s most regulated postcodes.",
  },
  {
    n: "03",
    title: "Independent by construction",
    desc: "No custody of client capital. No brokered transactions. No vendor commissions accepted. The only fee we earn is the one you sign for in writing.",
  },
];

export const interests = [
  "Owner-side Advisory",
  "Refurbishment Oversight",
  "PropTech & Retainer",
  "Full Portfolio Mandate",
  "Something else",
];
